import React, { useState, useEffect, useMemo } from 'react';
import { 
    XMarkIcon, 
    MagnifyingGlassIcon, 
    CurrencyDollarIcon,
    InformationCircleIcon,
    ArrowUpTrayIcon,
    ArrowDownTrayIcon,
    CheckIcon,
    BanknotesIcon
} from '@heroicons/react/24/outline';
import { 
    Drawer, 
    IconButton, 
    Paper, 
    Typography, 
    TextField, 
    InputAdornment, 
    CircularProgress,
    Tooltip
} from '@mui/material';
import styles from '../../styles/aICostTracking/TokenCostSettingsDrawer.module.scss';
import ButtonComponent from '../customButton';
import { fetchLLMCostList, updateLLMCost } from '../../utils/api/costAnalysisApis';
import { STATUS } from '../../utils/api/sharedfunction';

const TokenCostSettingsDrawer = ({ isOpen, onClose, onSave }) => {
    const [llmCosts, setLlmCosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [costs, setCosts] = useState({}); // { id: { inputTokensCost: 0, outputTokensCost: 0 } }

    useEffect(() => {
        if (isOpen) {
            fetchLLMCosts();
        }
    }, [isOpen]);

    const fetchLLMCosts = async () => {
        setLoading(true);
        try {
            const response = await fetchLLMCostList();
            let fetchedCosts = response.status === STATUS.SUCCESS ? (response.data || []) : [];
            
            // Fallback to dummy data if no costs are found (for UI demonstration)
            if (fetchedCosts.length === 0) {
                fetchedCosts = [
                    { id: 1, modelVersion: 'gpt-4o', inputTokensCost: 0.005, outputTokensCost: 0.015 },
                    { id: 2, modelVersion: 'gpt-4o-mini', inputTokensCost: 0.00015, outputTokensCost: 0.0006 },
                    { id: 3, modelVersion: 'gemini-2.0-flash', inputTokensCost: 0.0001, outputTokensCost: 0.0004 },
                ];
            }

            setLlmCosts(fetchedCosts);
            
            // Initialize costs from fetched data
            const initialCosts = {};
            fetchedCosts.forEach(cost => {
                initialCosts[cost.id] = {
                    inputTokensCost: cost.inputTokensCost || 0,
                    outputTokensCost: cost.outputTokensCost || 0
                };
            });
            setCosts(initialCosts);
        } catch (error) {
            console.error("Error fetching LLM costs:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCostChange = (id, type, value) => {
        // Allow only numbers and decimal
        if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
        
        setCosts(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [type]: value
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Prepare data for API - only include changed costs
            const costUpdates = Object.entries(costs)
                .filter(([id, costData]) => {
                    const original = llmCosts.find(cost => cost.id.toString() === id.toString());
                    return original && (
                        parseFloat(costData.inputTokensCost) !== original.inputTokensCost ||
                        parseFloat(costData.outputTokensCost) !== original.outputTokensCost
                    );
                })
                .map(([id, costData]) => ({
                    id: parseInt(id),
                    inputTokensCost: parseFloat(costData.inputTokensCost) || 0,
                    outputTokensCost: parseFloat(costData.outputTokensCost) || 0
                }));
            
            if (costUpdates.length > 0) {
                const response = await updateLLMCost(costUpdates);
                if (response.status === STATUS.SUCCESS) {
                    if (onSave) {
                        await onSave(costUpdates);
                    }
                    onClose();
                } else {
                    console.error("Failed to update LLM costs:", response.error);
                }
            } else {
                onClose(); // No changes to save
            }
        } catch (error) {
            console.error("Error saving LLM costs:", error);
        } finally {
            setSaving(false);
        }
    };

    const filteredLLMCosts = useMemo(() => {
        return llmCosts.filter(cost => 
            cost.modelVersion?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [llmCosts, searchQuery]);

    const hasChanges = useMemo(() => {
        // Check if any cost has been modified from original values
        return Object.entries(costs).some(([id, costData]) => {
            const original = llmCosts.find(cost => cost.id.toString() === id.toString());
            return original && (
                parseFloat(costData.inputTokensCost) !== original.inputTokensCost ||
                parseFloat(costData.outputTokensCost) !== original.outputTokensCost
            );
        });
    }, [costs, llmCosts]);

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            className={styles.sliderContainer}
            BackdropProps={{
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' }
            }}
        >
            <Paper className={styles.innerContainer}>
                {/* Header */}
                <div className={styles.headerContainer}>
                    <div className={styles.headerLeft}>
                        <div className={styles.iconCover}>
                            <BanknotesIcon style={{ width: 24, height: 24, color: '#fff' }} />
                        </div>
                        <div className={styles.contentContainer}>
                            <h3 className={styles.title}>Token Cost Settings</h3>
                            <p className={styles.subtitle}>Configure per-token costs for each AI model</p>
                        </div>
                    </div>
                    <IconButton onClick={onClose} className={styles.closeIcon}>
                        <XMarkIcon style={{ width: 20, height: 20 }} />
                    </IconButton>
                </div>

                {/* Body */}
                <div className={styles.bodyContainer}>
                    <div className={styles.infoSection}>
                        <div className={styles.searchWrapper}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by model version..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MagnifyingGlassIcon style={{ width: 18, height: 18, color: '#94a3b8' }} />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: '8px', backgroundColor: '#f8fafc' }
                                }}
                            />
                        </div>
                        <div className={styles.noteText}>
                            <InformationCircleIcon style={{ width: 16, height: 16 }} />
                            <span>Prices are typically set per 1,000,000 tokens (1M tokens).</span>
                        </div>
                    </div>

                    {loading ? (
                        <div className={styles.emptyState}>
                            <CircularProgress size={40} />
                            <p>Loading LLM costs...</p>
                        </div>
                    ) : filteredLLMCosts.length > 0 ? (
                        <table className={styles.tableContainer}>
                            <thead>
                                <tr>
                                    <th>Model Version</th>
                                    <th>Input Token Cost (per 1M)</th>
                                    <th>Output Token Cost (per 1M)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLLMCosts.map((cost) => (
                                    <tr key={cost.id}>
                                        <td>
                                            <div className={styles.modelInfo}>
                                                <span className={styles.modelName}>{cost.modelVersion}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.costInput}>
                                                <span className={styles.currencySymbol}>$</span>
                                                <input 
                                                    type="text" 
                                                    value={costs[cost.id]?.inputTokensCost ?? ''}
                                                    onChange={(e) => handleCostChange(cost.id, 'inputTokensCost', e.target.value)}
                                                    placeholder="0.00"
                                                />
                                                <Tooltip title="Input Token Cost">
                                                    <ArrowDownTrayIcon style={{ width: 14, height: 14, color: '#94a3b8' }} />
                                                </Tooltip>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.costInput}>
                                                <span className={styles.currencySymbol}>$</span>
                                                <input 
                                                    type="text" 
                                                    value={costs[cost.id]?.outputTokensCost ?? ''}
                                                    onChange={(e) => handleCostChange(cost.id, 'outputTokensCost', e.target.value)}
                                                    placeholder="0.00"
                                                />
                                                <Tooltip title="Output Token Cost">
                                                    <ArrowUpTrayIcon style={{ width: 14, height: 14, color: '#94a3b8' }} />
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className={styles.emptyState}>
                            <BanknotesIcon className={styles.emptyIcon} />
                            <p>{searchQuery ? 'No models found matching your search' : 'No LLM costs available'}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={styles.footerContainer}>
                    <div className={styles.footerLeft}>
                        <span>Updating costs for {llmCosts.length} models</span>
                    </div>
                    <div className={styles.footerRight}>
                        <ButtonComponent
                            variant="outline"
                            ctaText="Cancel"
                            onClick={onClose}
                        />
                        <ButtonComponent
                            variant="primary"
                            ctaText={saving ? "Saving..." : "Save Costs"}
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                            startIcon={!saving && <CheckIcon style={{ width: 18, height: 18, color: '#fff' }} />}
                        />
                    </div>
                </div>
            </Paper>
        </Drawer>
    );
};

export default TokenCostSettingsDrawer;

